import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./ParentConsentPopup.css";

export default function AadhaarVerificationPopup({
  visible,
  userPhoneNumber,
  onCancel,
  onSuccess,
}) {
  const [idNumber, setIdNumber] = useState("");
  const [referenceId, setReferenceId] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [countdown]);

  useEffect(() => {
    if (!visible) {
      setIdNumber("");
      setReferenceId(null);
      setOtpCode("");
      setCountdown(0);
      clearInterval(timerRef.current);
    }
  }, [visible]);

  const handleRequestOTP = async () => {
    if (idNumber.trim().length !== 12) {
      return toast.warn("Please enter a valid 12-digit Aadhaar number.");
    }
    if (!userPhoneNumber) {
      return toast.error("Phone number missing. Please update the form.");
    }

    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/send_aadhar_otp`, {
        aadhaar_number: idNumber,
        phone_number: userPhoneNumber,
        user_type: "pediatrician",
      });

      if (res.data && res.data.success && res.data.reference_id) {
        setReferenceId(res.data.reference_id);
        setCountdown(60);
        toast.success("Verification code dispatched successfully.");
      } else {
        toast.error(res.data?.message || res.data?.error || "Failed to initiate verification.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to connect to verification services.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.trim().length !== 6) {
      return toast.warn("Please enter the 6-digit verification code.");
    }

    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/verify_aadhar_otp`, {
        reference_id: referenceId,
        otp: otpCode,
      });

      if (res.data && res.data.success) {
        onSuccess({
          aadhaar_number: idNumber,
          transactionReceipt: res.data?.transaction_receipt,
        });
      } else {
        toast.error(res.data?.message || res.data?.error || "Incorrect validation code.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error finalizing verification state.");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div className="term-privacy-overlay" onClick={!loading ? onCancel : undefined}></div>
      <div className="term-privacy-popup consent-popup-card">
        <div className="consent-header-block">
          <h2 className="consent-brand-title">Confirm Your Identity</h2>
          <p className="consent-sub-title">Identity Verification — confirm it's really you</p>
        </div>

        <div className="verify-inner-layout">
          <p className="verify-instruction-text" style={{textAlign: "center", marginBottom: "20px"}}>
            {!referenceId
              ? "Enter your 12-digit number to verify your account."
              : "We've sent a 6-digit secure code to your registered mobile number."}
          </p>

          <div className="verify-field-block">
            <label className="verify-field-label">Aadhaar Number</label>
            <input
              type="text"
              className="verify-core-input-id"
              maxLength={12}
              placeholder="Enter 12-digit Aadhaar number"
              value={idNumber}
              onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ""))}
              disabled={loading || !!referenceId}
            />
          </div>

          {!referenceId && (
            <button type="button" className="verify-submit-btn" onClick={handleRequestOTP} disabled={loading} style={{width: "100%", padding: "14px"}}>
              {loading ? "Sending..." : "Get Verification Code"}
            </button>
          )}

          {!!referenceId && (
            <div style={{ width: "100%" }}>
              <div className="verify-field-block">
                <label className="verify-field-label">Enter OTP</label>
                <input
                  type="text"
                  className="verify-core-input-otp"
                  maxLength={6}
                  placeholder="X X X X X X"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className="verify-resend-link-container" style={{textAlign: "center", marginBottom: "20px"}}>
                {countdown > 0 ? (
                  <span className="verify-resend-countdown">Resend code in {countdown}s</span>
                ) : (
                  <span className="verify-resend-clickable" style={{color: "var(--color-primary)", cursor: "pointer"}} onClick={!loading ? handleRequestOTP : undefined}>
                    Didn't receive code? Resend
                  </span>
                )}
              </div>

              <button type="button" className="verify-submit-btn" onClick={handleVerifyOTP} disabled={loading} style={{width: "100%", padding: "14px"}}>
                {loading ? "Verifying..." : "Verify & Continue"}
              </button>
            </div>
          )}

          <div className="verify-cancel-btn-wrapper" style={{textAlign: "center", marginTop: "15px"}}>
            <span className="verify-cancel-link-text" style={{cursor: "pointer", color: "#666"}} onClick={!loading ? onCancel : undefined}>
              Cancel
            </span>
          </div>
        </div>
      </div>
    </>
  );
}